import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionTemplate } from "framer-motion";

/**
 * Canon EF · macro scroll experience — left/right split.
 *
 *   ┌──────────────────────┬──────────────────────┐
 *   │                      │   Eyebrow            │
 *   │   Canon EF lens      │   « Quote »          │
 *   │   (animated SVG)     │   Author attribution │
 *   │                      │                      │
 *   └──────────────────────┴──────────────────────┘
 *
 * • Lens is photoreal Canon EF (white-body L-series, red ring, focus scale).
 * • Two-beat scroll: emergence, then aperture shutter snap with white flash.
 * • Aperture diameter is locked to the front element diameter.
 * • Motion is spring-smoothed for buttery, fluid feel.
 */
export const CameraScroll = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Heavier damping = smoother, slower easing
  const p = useSpring(scrollYProgress, { stiffness: 70, damping: 28, mass: 0.6 });

  // ─── Beat 1 : lens emergence ───
  const lensScale = useTransform(p, [0, 0.32, 0.6, 1], [0.78, 1.0, 1.06, 1.08]);
  const lensOpacity = useTransform(p, [0, 0.14, 0.85, 1], [0, 1, 1, 0.9]);
  const lensRotate = useTransform(p, [0, 1], [-3, 2]);
  const lensY = useTransform(p, [0, 0.5, 1], [40, 0, -10]);
  const lensBlur = useTransform(p, [0, 0.25, 0.5], [8, 0.5, 0]);
  const lensFilter = useMotionTemplate`blur(${lensBlur}px)`;

  // ─── Beat 2 : shutter snap (second scroll) ───
  const apertureClose = useTransform(p, [0.30, 0.40, 0.44, 0.54], [0, 1, 1, 0]);
  const flashOpacity = useTransform(p, [0.38, 0.43, 0.50], [0, 0.7, 0]);

  // Right-side reveal stages
  const eyebrowOpacity = useTransform(p, [0.04, 0.18], [0, 1]);
  const quoteOpacity = useTransform(p, [0.15, 0.32], [0, 1]);
  const quoteY = useTransform(p, [0.15, 0.32], [22, 0]);
  const attribOpacity = useTransform(p, [0.48, 0.62], [0, 1]);
  const attribY = useTransform(p, [0.48, 0.62], [16, 0]);
  const techOpacity = useTransform(p, [0.65, 0.78], [0, 1]);

  // Internal glass reflection drift
  const reflectionX = useTransform(p, [0, 1], [-5, 5]);

  return (
    <section
      ref={ref}
      data-testid="camera-scroll"
      className="relative bg-ink"
      style={{ height: "110vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-ink grid grid-cols-1 md:grid-cols-2">
        {/* Soft floor gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center 70%, rgba(184,169,154,0.05) 0%, rgba(0,0,0,0) 50%)",
          }}
        />

        {/* ── LEFT half : the lens ── */}
        <div className="relative flex items-center justify-center px-6">
          <motion.div
            style={{
              scale: lensScale,
              opacity: lensOpacity,
              rotate: lensRotate,
              y: lensY,
              filter: lensFilter,
            }}
            className="will-change-transform"
          >
            <CanonLensSVG apertureClose={apertureClose} reflectionX={reflectionX} />
          </motion.div>

          {/* White flash over the lens area */}
          <motion.div
            style={{ opacity: flashOpacity }}
            className="absolute inset-0 bg-white pointer-events-none z-40 mix-blend-screen"
          />
        </div>

        {/* ── RIGHT half : the quote ── */}
        <div className="relative flex items-center px-8 md:px-14 lg:px-20 z-30">
          <div className="max-w-xl">
            <motion.div style={{ opacity: eyebrowOpacity }} className="eyebrow text-stone mb-8">
              Canon EF · L-Series · f/2.8
            </motion.div>

            <motion.blockquote
              style={{ opacity: quoteOpacity, y: quoteY }}
              className="font-display italic text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-bone"
            >
              « Photographier, c{"'"}est mettre sur la
              <span className="text-stone"> même ligne de mire </span>
              la tête, l{"'"}œil et le cœur. »
            </motion.blockquote>

            <motion.div
              style={{ opacity: attribOpacity, y: attribY }}
              className="mt-10 flex items-center gap-4"
            >
              <span className="block w-12 h-px bg-stone" />
              <div>
                <div className="font-display text-xl text-bone">Henri Cartier-Bresson</div>
                <div className="eyebrow text-stone/70 mt-1">Photographe · 1908–2004</div>
              </div>
            </motion.div>

            <motion.div
              style={{ opacity: techOpacity }}
              className="mt-12 grid grid-cols-3 gap-4 border-t border-stone/20 pt-4 text-[10px] tracking-[0.28em] uppercase text-bone/55"
            >
              <span>EF 24-70mm</span>
              <span>1/4000 s</span>
              <span>ISO 100</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================ */
/*  Photo-real Canon EF L-Series lens — front view, all SVG     */
/* ============================================================ */
const GLASS_R = 118;

const CanonLensSVG = ({ apertureClose, reflectionX }) => {
  const BLADE_COUNT = 9; // Canon EF L typical
  const blades = Array.from({ length: BLADE_COUNT });

  return (
    <svg
      viewBox="-200 -200 400 400"
      width="min(46vw, 560px)"
      height="min(46vw, 560px)"
      role="img"
      aria-label="Canon EF L-series lens"
      style={{ filter: "drop-shadow(0 35px 70px rgba(0,0,0,0.7))" }}
    >
      <defs>
        {/* Canon white EF body */}
        <radialGradient id="canonBody" cx="50%" cy="40%" r="58%">
          <stop offset="0%" stopColor="#f0ece4" />
          <stop offset="55%" stopColor="#d8d2c4" />
          <stop offset="100%" stopColor="#9a948a" />
        </radialGradient>
        {/* Inner barrel */}
        <radialGradient id="innerBarrel" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#040404" />
        </radialGradient>
        {/* Front element glass */}
        <radialGradient id="canonGlass" cx="40%" cy="30%" r="65%">
          <stop offset="0%" stopColor="#142028" />
          <stop offset="30%" stopColor="#0a131a" />
          <stop offset="65%" stopColor="#040809" />
          <stop offset="100%" stopColor="#000" />
        </radialGradient>
        <linearGradient id="canonCoat" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2a4a6a" stopOpacity="0.4" />
          <stop offset="60%" stopColor="#4a2a4a" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="canonReflect" cx="32%" cy="26%" r="38%">
          <stop offset="0%" stopColor="#e8ecf0" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#e8ecf0" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#e8ecf0" stopOpacity="0" />
        </radialGradient>
        {/* Knurled rubber zoom ring */}
        <pattern id="canonKnurl" x="0" y="0" width="3" height="6" patternUnits="userSpaceOnUse">
          <rect width="3" height="6" fill="#0a0a0a" />
          <rect x="0" y="0" width="1.5" height="6" fill="#1a1a1a" />
        </pattern>
        {/* Aperture blade fill */}
        <linearGradient id="canonBlade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a0a0a" />
          <stop offset="55%" stopColor="#161616" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        <clipPath id="canonGlassClip">
          <circle cx="0" cy="0" r={GLASS_R} />
        </clipPath>
        <filter id="canonSoft" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" />
        </filter>
      </defs>

      {/* Outer rim shadow */}
      <circle cx="0" cy="0" r="184" fill="#000" />

      {/* White Canon EF body */}
      <circle cx="0" cy="0" r="180" fill="url(#canonBody)" />

      {/* Outer brand band with engraving */}
      <circle cx="0" cy="0" r="175" fill="none" stroke="#2a2a2a" strokeOpacity="0.25" strokeWidth="0.8" />
      <g fontFamily="Inter, sans-serif" fill="#1a1a1a">
        <text x="0" y="-155" textAnchor="middle" fontSize="13" letterSpacing="2.4" fontWeight="700" fillOpacity="0.85">
          Canon
        </text>
        <text x="0" y="-140" textAnchor="middle" fontSize="6" letterSpacing="3" fontWeight="500" fillOpacity="0.55">
          ZOOM LENS
        </text>
        <text x="0" y="165" textAnchor="middle" fontSize="6.4" letterSpacing="2.4" fontWeight="600" fillOpacity="0.65">
          EF 24-70mm 1:2.8 L II USM
        </text>
        <text x="-150" y="6" textAnchor="middle" fontSize="5" letterSpacing="1.5" fillOpacity="0.45">
          Ø82mm
        </text>
        <text x="150" y="6" textAnchor="middle" fontSize="5" letterSpacing="1.5" fillOpacity="0.45">
          IS
        </text>
      </g>

      {/* Canon signature RED RING — L-series identifier */}
      <circle cx="0" cy="0" r="166" fill="none" stroke="#cf2e1f" strokeWidth="5" />
      <circle cx="0" cy="0" r="166" fill="none" stroke="#7a1a14" strokeWidth="0.6" strokeOpacity="0.6" />

      {/* Knurled rubber zoom ring */}
      <circle cx="0" cy="0" r="155" fill="none" stroke="url(#canonKnurl)" strokeWidth="14" />
      <circle cx="0" cy="0" r="148" fill="none" stroke="#000" strokeWidth="0.6" />
      <circle cx="0" cy="0" r="162" fill="none" stroke="#000" strokeWidth="0.6" />

      {/* Inner barrel leading to glass */}
      <circle cx="0" cy="0" r="140" fill="url(#innerBarrel)" />
      <circle cx="0" cy="0" r="140" fill="none" stroke="#000" strokeWidth="0.8" />
      <circle cx="0" cy="0" r="132" fill="none" stroke="#000" strokeWidth="0.5" />
      <circle cx="0" cy="0" r="126" fill="none" stroke="#0a0a0a" strokeWidth="0.6" />

      {/* Front element glass + aperture */}
      <g clipPath="url(#canonGlassClip)">
        <circle cx="0" cy="0" r={GLASS_R} fill="url(#canonGlass)" />
        <circle cx="0" cy="0" r={GLASS_R} fill="url(#canonCoat)" />
        <g stroke="#B8A99A" strokeOpacity="0.05" strokeWidth="0.4" fill="none">
          <circle cx="0" cy="0" r="95" />
          <circle cx="0" cy="0" r="70" />
          <circle cx="0" cy="0" r="40" />
        </g>

        {/* 9-blade aperture */}
        <g>
          {blades.map((_, i) => {
            const angle = (360 / BLADE_COUNT) * i;
            return <Blade key={i} angle={angle} closeProgress={apertureClose} />;
          })}
        </g>

        {/* Catch-light when closed */}
        <motion.circle
          cx="0"
          cy="0"
          r="1.3"
          fill="#fff"
          style={{ opacity: useTransform(apertureClose, [0, 0.65, 1], [0, 0, 1]) }}
        />

        {/* Glass reflection */}
        <motion.ellipse
          cx="-34"
          cy="-44"
          rx="46"
          ry="28"
          fill="url(#canonReflect)"
          filter="url(#canonSoft)"
          style={{ x: reflectionX }}
        />
        <motion.circle cx="32" cy="-58" r="3" fill="#fff" opacity="0.18" style={{ x: reflectionX }} />
      </g>

      {/* Glass edge bevel */}
      <circle cx="0" cy="0" r={GLASS_R} fill="none" stroke="#000" strokeWidth="1" />
      <circle cx="0" cy="0" r={GLASS_R + 1} fill="none" stroke="#1f1f1f" strokeWidth="0.4" />
      <circle cx="0" cy="0" r={GLASS_R - 1} fill="none" stroke="#000" strokeWidth="0.4" />

      {/* Outer perimeter */}
      <circle cx="0" cy="0" r="180" fill="none" stroke="#7a766c" strokeWidth="0.6" />
      <circle cx="0" cy="0" r="184" fill="none" stroke="#000" strokeWidth="1.2" />
    </svg>
  );
};

/**
 * Single Canon aperture blade — anchored at glass edge,
 * extends inward, 9 blades form the iris.
 */
const Blade = ({ angle, closeProgress }) => {
  // For 9 blades: half-width = GLASS_R * tan(π/9) ≈ GLASS_R * 0.3640 ≈ 43
  const W = 43;
  const tipY = -GLASS_R;
  const baseY = 4;
  const d = `M 0 ${tipY}
             Q ${W} ${tipY * 0.5} ${W} ${baseY}
             Q 0 ${baseY + 10} ${-W} ${baseY}
             Q ${-W} ${tipY * 0.5} 0 ${tipY} Z`;

  const translateY = useTransform(closeProgress, [0, 1], [-(GLASS_R + 16), 0]);
  const opacity = useTransform(closeProgress, [0, 0.04, 1], [0, 0.98, 1]);

  return (
    <motion.g style={{ rotate: angle }}>
      {/* Shadow */}
      <motion.path
        d={d}
        fill="#000"
        fillOpacity="0.55"
        style={{ y: useTransform(closeProgress, [0, 1], [-(GLASS_R + 12), 3]), opacity }}
      />
      {/* Body */}
      <motion.path
        d={d}
        fill="url(#canonBlade)"
        stroke="#1a1a1a"
        strokeWidth="0.5"
        style={{ y: translateY, opacity }}
      />
      {/* Highlight edge */}
      <motion.path
        d={`M 0 ${tipY} Q ${W} ${tipY * 0.5} ${W} ${baseY}`}
        stroke="#B8A99A"
        strokeOpacity="0.2"
        strokeWidth="0.55"
        fill="none"
        style={{ y: translateY, opacity }}
      />
    </motion.g>
  );
};

export default CameraScroll;
